import { useEffect, useId, useState } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

interface BeneficiaryAddressProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  showStarLabel?: boolean;
  disable?: boolean;
  max?: number;
  minLength?: number;
  triggerValidation?: boolean;
  fieldName?: string;
  onValidate?: (field: string, isValid: boolean) => void;
}

const BeneficiaryAddress: React.FC<BeneficiaryAddressProps> = ({
  label = "",
  value,
  onChange,
  validationMessage,
  showStarLabel = true,
  disable = false,
  max = 200,
  minLength = 4,
  triggerValidation = false,
  fieldName = "beneficiaryAddress",
  onValidate,
}) => {
  const { t } = useTranslation();

  const inputId = useId();
  const errorId = `${inputId}-error`;

  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const sanitize = (s: string) => {
    let cleaned = s.replaceAll(/[^\w\s.,]/g, "");
    cleaned = cleaned.replaceAll(/\s+/g, " ");
    return cleaned.slice(0, max);
  };

  const validate = (val: string): string => {
    if (!val) {
      return `${t("please_enter_the")} ${label}.`;
    }

    if (val.length < minLength) {
      return `${t("address_must_be_at_least")} ${minLength} ${t(
        "characters",
      )}.`;
    }

    if (val.length > max) {
      return `${t("address_cannot_exceed")} ${max} ${t("characters")}.`;
    }

    return "";
  };

  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    onValidate?.(fieldName, !newError);
    return newError;
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    const filtered = sanitize(raw);

    onChange(filtered);

    if (touched) {
      setError(validate(filtered));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);
    updateValidation(value);
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(value);
    }
  }, [validationMessage]);

  const isInvalid = !!error && touched;

  return (
    <div className="gx_input_container">
      <label
        htmlFor={inputId}
        className={`input-label gx-font-semibold ${
          value || isFocused ? "floating" : ""
        }`}
      >
        {t(label)}

        {showStarLabel && (
          <>
            <span
              aria-hidden="true"
              className={`gx_input_star ${
                value ? "gx_input_star_float" : "gx_input_star_normal"
              }`}
            >
              *
            </span>

            <span className="sr-only">Required field</span>
          </>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <img
          src={`${ICON_BASE_PATH}beneficiaryaddress.svg`}
          alt=""
          aria-hidden="true"
        />

        <TextBox
          id={inputId}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          type="text"
          autoComplete="street-address"
          disabled={disable}
          max={max}
          className="gx-textbox-css"
          aria-required={showStarLabel}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? errorId : undefined}
        />
      </div>

      {isInvalid && (
        <div
          id={errorId}
          className="gx_input_validationmessage gx-font-regular"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryAddress;
